import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ContractContext } from '@nile/client_management/src/Contracts/Contexts/ContractContext';

import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  ButtonGroup,
  DisplayText,
  Spinner,
  TextField
} from '@superops/ui-design-system';

import {
  CustomMenu,
  getTranslatedText,
  reactSelectInlineError
} from '@nile/components';
import {
  BillableContract,
  ContractType,
  ServiceCatalogItem,
  ServiceCatalogItemType,
  ServiceTypeItemOfferingType
} from '@nile/types';
import ServiceCatalogModal from '@nile/banyan/src/Pages/ServiceCatalogNew/ServiceCatatlogModal';

import { MinimalDropDown } from './MinimalDropDown';
import { cloneDeep, omit } from 'lodash';
import {
  AccountClient,
  CREATE_SERVICE_CATALOG,
  CREATE_SERVICE_CATALOG_FROM_CONTRACT,
  GET_SERVICE_CATALOG_ITEMS,
  UPDATE_SERVICE_CATALOG
} from '@nile/graphql';
import {
  BILLABLE_CONTRACT_DEFAULT_VALUE_MAP,
  ServiceCatalogConditionsBasedOnContractType
} from '../constants';
import { Tooltip } from 'react-clone-tippy';
import { GET_APPLICABLE_PROVISION_RULE_LIST } from '@nile/graphql/src/Query/contracts';

type ServiceCatalogOptionsType = 'PRODUCT' | 'SERVICE' | 'PACKAGE';

const groupNameKeys = {
  PRODUCT: 'admin.serviceCatalog.type.product.create',
  PACKAGE: 'admin.serviceCatalog.type.package.create',
  SERVICE: 'admin.serviceCatalog.type.service.create',
  PACKAGE_BUNDLE: 'admin.serviceCatalog.type.package.create'
};

const CustomMenuList = ({ groups, selectedGroup, onGroupChange }) => {
  return ({ children, ...props }) => {
    // Filtering the option groups to just render the one that has been selected or all of them.
    // const filteredChildren = React.Children.toArray(children).filter(
    //   (group, index) => index === selectedGroup
    // );

    if (groups?.length > 1) {
      return (
        <div
          className="min-width-100 max-height-250 overflow-y-auto overflow-x-auto"
          {...props}
        >
          <ButtonGroup segmented spacing="tight" fullWidth>
            {groups.map((groupName) => (
              <Button
                size="medium"
                icon={
                  <SVGIcon
                    size="xs"
                    name={groupName === 'SERVICE' ? 'Cart' : 'Puzzle'}
                  />
                }
                onClick={() => onGroupChange(groupName)}
                outline={groupName === selectedGroup}
                selected={groupName === selectedGroup}
              >
                <DisplayText size="small">{groupName}</DisplayText>
              </Button>
            ))}
          </ButtonGroup>
          {children}
        </div>
      );
    }
    return (
      <div
        className="min-width-100 max-height-250 overflow-y-auto overflow-x-auto"
        {...props}
      >
        {children}
      </div>
    );
  };
};

const CustomMenuWithActions = ({ selectedGroup, onClick }) => {
  return (allprops) => {
    const { innerRef, innerProps, isDisabled, children } = allprops;
    if (!!1) {
      return (
        <div>
          <CustomMenu
            {...allprops}
            actionComponent={
              <div className="ellipsis full-width cp" onClick={onClick}>
                <div className="d-in-f center linkText">
                  <SVGIcon name="Add" color="rgba(1, 31, 255, 1)" />
                  <div className="ml-5">
                    <FormattedMessage id={groupNameKeys[selectedGroup]} />
                  </div>
                </div>
              </div>
            }
          />
        </div>
      );
    }
    return !isDisabled ? (
      <div ref={innerRef} {...innerProps} className={`customReactSelectMenu`}>
        {children}
      </div>
    ) : null;
  };
};

interface ChargeItemDropDownProps {
  contractType: ContractType;
  billableContract?: BillableContract;
  updateContract?: (key, value) => void;
  onChange: (fieldName) => any;
  readOnly?: boolean;
  error?: string;
}

export const ChargeItemDropdown = ({
  contractType,
  billableContract,
  updateContract,
  onChange,
  readOnly,
  error
}: ChargeItemDropDownProps) => {
  const intl = useIntl();

  const { client, template } = useContext(ContractContext);

  const [serviceCatalogItems, setServiceCatalogItems] = useState<
    ServiceCatalogItem[]
  >([]);
  const {
    loading,
    data,
    error: getServiceCatalogItemsError,
    refetch
  } = useQuery(GET_SERVICE_CATALOG_ITEMS, {
    skip: !contractType,
    client: AccountClient,
    fetchPolicy: 'no-cache',
    variables: {
      listInfo: {
        condition: ServiceCatalogConditionsBasedOnContractType[contractType]
      }
    }
  });

  useEffect(() => {
    if (data?.getServiceCatalogItems?.items?.length) {
      setServiceCatalogItems(data?.getServiceCatalogItems?.items);
    }
  }, [data?.getServiceCatalogItems?.items]);

  const serviceCatalogFormRef = useRef<{
    showForm: ({
      mode,
      catalogType,
      catalogItemId
    }: {
      mode: 'NEW' | 'EDIT' | 'CLONE';
      catalogType?:
        | ServiceCatalogOptionsType
        | 'PACKAGE_BUNDLE'
        | 'SERVICE_QUANTITY';
      catalogItemId?: string;
    }) => void;
    hideForm: () => void;
  }>(null);

  const [selectedGroup, setSelectedGroup] = useState<
    ServiceCatalogOptionsType | 'PACKAGE_BUNDLE' | 'SERVICE_QUANTITY' | null
  >(null);

  const [UpdateServiceCatalogItem] = useMutation(UPDATE_SERVICE_CATALOG, {
    client: AccountClient,
    onCompleted({ updateServiceCatalogItem }) {
      if (updateServiceCatalogItem) {
        serviceCatalogFormRef.current?.hideForm();
      }
    }
  });

  function saveServiceCatalogItem(type: 'UPDATE' | 'CREATE') {
    return ({
      formData,
      createNew
    }: {
      formData: ServiceCatalogItem;
      createNew?: boolean;
    }): Promise<unknown> => {
      if (type === 'UPDATE') {
        return UpdateServiceCatalogItem({
          variables: {
            input: omit(formData, 'itemType')
          }
        });
      } else {
        return CreateServiceCatalogItem({
          variables: {
            input: formData
          }
        });
      }
    };
  }

  function createNewChargeItem() {
    serviceCatalogFormRef.current?.showForm({
      mode: 'NEW',
      catalogType:
        selectedGroup === 'SERVICE' ? 'SERVICE_QUANTITY' : selectedGroup
    });
  }

  const [getApplicableProvisionRule] = useLazyQuery(
    GET_APPLICABLE_PROVISION_RULE_LIST,
    {
      client: AccountClient
    }
  );

  async function optionSelectHandler(selected) {
    // const { data: applicableProvisionRule } = await getApplicableProvisionRule({
    //   variables: {
    //     input: {
    //       ...(template?.templateId
    //         ? {
    //             template: {
    //               templateId: template?.templateId
    //             }
    //           }
    //         : {
    //             client: {
    //               accountId: client?.accountId
    //             }
    //           }),
    //       serviceCatalogItems: selected?.serviceTypeItem?.bundledItems?.map(
    //         (item) => ({
    //           itemId: item?.itemId
    //         })
    //       ),
    //       provisionRule: billableContract?.provisionRule?.provisionRuleId
    //         ? {
    //             provisionRuleId:
    //               billableContract?.provisionRule?.provisionRuleId
    //           }
    //         : null
    //     }
    //   }
    // });

    onChange({
      ...BILLABLE_CONTRACT_DEFAULT_VALUE_MAP[contractType],
      includedItemList: selected?.serviceTypeItem?.bundledItems?.map((item) => {
        return {
          serviceCatalogItem: item,
          provisionRule: null
        };
      }),
      chargeItem: omit(selected, ['label', 'value', 'groupName']),
      sellingPriceOverridden: !selected?.sellingPrice,
      sellingPrice:
        selected?.serviceTypeItem?.offeringType === 'BLOCK_MONEY'
          ? {
              model: 'PER_UNIT',
              details: [
                {
                  ...billableContract?.sellingPrice?.details?.[0],
                  value:
                    selected?.serviceTypeItem?.redeemableServiceBundle
                      ?.redeemableQuantity
                }
              ]
            }
          : selected?.sellingPrice
          ? selected?.sellingPrice
          : {
              model: 'PER_UNIT',
              details: [
                {
                  value: null
                }
              ]
            },
      ...(['BLOCK_HOUR', 'BLOCK_MONEY'].includes(
        selected?.serviceTypeItem?.offeringType
      ) && {
        redeemableContract: {
          ...billableContract?.redeemableContract,
          applicableHours:
            selected?.serviceTypeItem?.redeemableServiceBundle?.applicableHours,
          applicableType:
            selected?.serviceTypeItem?.redeemableServiceBundle?.applicableType
        }
      }),
      ...(['BLOCK_HOUR'].includes(selected?.serviceTypeItem?.offeringType)
        ? {
            cumulatedQuantity:
              selected?.serviceTypeItem?.redeemableServiceBundle
                ?.redeemableQuantity || '1',
            quantityCalculationType: 'FIXED',
            changes: [
              {
                ...billableContract?.changes?.[0],
                quantity:
                  selected?.serviceTypeItem?.redeemableServiceBundle
                    ?.redeemableQuantity || '1'
              }
            ]
          }
        : {
            cumulatedQuantity: null,
            quantityCalculationType: 'FIXED',
            changes: [
              {
                ...billableContract?.changes?.[0],
                quantity: null
              }
            ]
          })
    });
    updateContract('description', selected?.description);
  }

  const [CreateServiceCatalogItem] = useMutation(
    CREATE_SERVICE_CATALOG_FROM_CONTRACT,
    {
      client: AccountClient,
      onCompleted({ createServiceCatalogItem }) {
        if (createServiceCatalogItem) {
          const serviceCatalogItemsClone = cloneDeep(serviceCatalogItems);
          serviceCatalogItemsClone.push(createServiceCatalogItem);
          setServiceCatalogItems(serviceCatalogItemsClone);
          optionSelectHandler(createServiceCatalogItem);
          serviceCatalogFormRef.current?.hideForm();
        }
      }
    }
  );

  const {
    groups,
    options
  }: {
    groups: ('SERVICE' | 'PRODUCT')[];
    options: {
      label: string;
      value: string;
      groupName: any;
    }[];
  } = useMemo(() => {
    const grps: any[] = [];
    const opts = serviceCatalogItems?.map((item: ServiceCatalogItem) => {
      let groupType: ServiceTypeItemOfferingType | ServiceCatalogItemType =
        item?.itemType || 'PRODUCT';
      if (
        ['BLOCK_HOUR', 'BLOCK_MONEY', 'SERVICE_BUNDLE'].includes(
          item?.serviceTypeItem?.offeringType
        )
      ) {
        groupType = 'PACKAGE';
      }

      if (!grps.includes(groupType)) {
        grps.push(groupType);
      }

      return {
        ...item,
        label: item?.name || '',
        value: item?.itemId || '',
        groupName: groupType
      };
    });

    setSelectedGroup(grps?.[0] || 'PACKAGE');

    return { groups: grps, options: opts };
  }, [serviceCatalogItems]);

  if (loading) {
    return <Spinner size="small" />;
  }

  const selectedOption = options?.find(
    (opt) => opt?.value === billableContract?.chargeItem?.itemId
  );

  return (
    <>
      <ServiceCatalogModal
        hideAddNew
        className={null}
        reference={serviceCatalogFormRef}
        saveServiceCatalogItem={saveServiceCatalogItem}
      />
      <div className="w200 wm-400 fg1">
        <Tooltip
          delay={300}
          position="bottom-start"
          trigger="mouseenter"
          disabled={!selectedOption?.label}
          html={<div className="p5 br-10">{selectedOption?.label}</div>}
        >
          <MinimalDropDown
            isDisabled={readOnly}
            value={selectedOption}
            placeholder={getTranslatedText(
              intl,
              contractType === 'USAGE'
                ? 'integrations.catalogItemSelect.placeholder'
                : 'msp.contracts.form.field.chargeItem.placeholder'
            )}
            options={options.filter((opt) => opt.groupName === selectedGroup)}
            components={{
              Menu: CustomMenuWithActions({
                selectedGroup,
                onClick: createNewChargeItem
              }),
              MenuList: CustomMenuList({
                groups,
                selectedGroup,
                onGroupChange: setSelectedGroup
              })
            }}
            onChange={optionSelectHandler}
            closeMenuOnScroll={false}
          />
          {error && reactSelectInlineError(error)}
        </Tooltip>
      </div>
    </>
  );
};
